"use client";

import { forwardRef, type CSSProperties } from "react";
import {
  LocalDigitalHuman,
  type LocalDigitalHumanHandle,
  type LocalDigitalHumanProps,
} from "./LocalDigitalHuman";

export const EXPERTS_ALIVE_SETTINGS_URL =
  "https://expertsalive.ai/sdk/runtime/approved-lip-sync-settings.json";

export interface ExpertViewProps extends Pick<
  LocalDigitalHumanProps,
  | "audioElement"
  | "assetManifestUrl"
  | "locale"
  | "onError"
  | "onEvent"
  | "onInputLevel"
  | "onProgress"
  | "onFirstFrameReady"
  | "onRuntimeDetached"
  | "onRuntimeDestroyed"
  | "onRuntimeDisposed"
  | "onRuntimeReady"
  | "onResolutionChange"
  | "onStateChange"
  | "plan"
  | "poolKey"
  | "persistentKey"
  | "poolGroupKey"
  | "estimatedBytes"
  | "desiredState"
  | "resolution"
> {
  className?: string;
  settingsUrl?: string;
  style?: CSSProperties;
}

/** Default iframe-free React view backed by the local Experts Alive runtime. */
export const ExpertView = forwardRef<LocalDigitalHumanHandle, ExpertViewProps>(
  function ExpertView({
    assetManifestUrl,
    className,
    locale = "en-US",
    plan = "free",
    resolution = "auto",
    settingsUrl = EXPERTS_ALIVE_SETTINGS_URL,
    style,
    ...callbacks
  }, ref) {
    return (
      <LocalDigitalHuman
        ref={ref}
        {...callbacks}
        assetManifestUrl={assetManifestUrl}
        className={className}
        locale={locale}
        motionClassName="experts-alive-motion"
        plan={plan}
        platform="webApp"
        resolution={resolution}
        settingsUrl={settingsUrl}
        style={{ aspectRatio: "3 / 4", maxWidth: "100%", overflow: "hidden", ...style }}
        placeholder={(
          <div aria-live="polite" style={{ display: "grid", height: "100%", placeItems: "center" }}>
            Loading Digital Human…
          </div>
        )}
      />
    );
  },
);
